<script setup lang="ts">
import { computed } from "vue";
import { teamPointsBreakdown } from "@shared/scoring";
import type { EntryWithScores, Team } from "@shared/types";
import Flag from "../common/Flag.vue";
import StatusBadge from "../common/StatusBadge.vue";

const props = defineProps<{ entry: EntryWithScores }>();

// Flatten a team's breakdown into the rows we actually want to show: only the
// components that scored anything, each with a label and its points.
function partsFor(team: Team) {
  const b = teamPointsBreakdown(team);
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;
  const rows = [
    { label: plural(b.wins.count, "win"), points: b.wins.points, show: b.wins.count > 0 },
    { label: plural(b.draws.count, "draw"), points: b.draws.points, show: b.draws.count > 0 },
    { label: plural(b.goals.count, "goal"), points: b.goals.points, show: b.goals.count > 0 },
    { label: plural(b.cleanSheets.count, "clean sheet"), points: b.cleanSheets.points, show: b.cleanSheets.count > 0 },
    { label: "round bonus", points: b.roundBonus.points, show: b.roundBonus.points > 0 },
  ];
  return rows.filter((r) => r.show);
}

const pots = computed(() => {
  const accent = ["text-pot1", "text-pot2", "text-pot3"] as const;
  return [props.entry.pot1, props.entry.pot2, props.entry.pot3].map((team, i) => {
    const base = teamPointsBreakdown(team).total;
    const multiplier = i === 2 ? 2 : 1;
    return {
      team,
      label: `Pot ${i + 1}`,
      accent: accent[i],
      parts: partsFor(team),
      base,
      multiplier,
      contribution: base * multiplier,
    };
  });
});
</script>

<template>
  <div class="bg-slate-50/70 px-3 py-3">
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <div
        v-for="pot in pots"
        :key="pot.label"
        class="rounded-lg bg-white ring-1 ring-slate-200 p-3 flex flex-col gap-2"
      >
        <div class="flex items-center gap-2">
          <Flag :code="pot.team.flag_code" :name="pot.team.name" size="sm" />
          <span class="font-semibold text-slate-800 text-sm truncate">{{ pot.team.name }}</span>
          <StatusBadge :team="pot.team" size="xs" />
          <span :class="['ml-auto text-[10px] uppercase tracking-wider font-bold', pot.accent]">{{ pot.label }}</span>
        </div>

        <ul v-if="pot.parts.length" class="text-xs text-slate-600 space-y-0.5">
          <li v-for="part in pot.parts" :key="part.label" class="flex justify-between tabular">
            <span>{{ part.label }}</span>
            <span class="font-medium text-slate-700">+{{ part.points }}</span>
          </li>
        </ul>
        <p v-else class="text-xs text-slate-400 italic">No points yet</p>

        <!-- Cards don't affect points, shown separately for the side prizes -->
        <div class="text-[11px] text-slate-400 tabular">
          🟨 {{ pot.team.yellows }} · 🟥 {{ pot.team.reds }}
        </div>

        <div class="mt-auto pt-2 border-t border-slate-100 flex justify-between items-baseline text-sm tabular">
          <span class="text-slate-500">
            Contributes
            <span v-if="pot.multiplier > 1" class="text-[11px] text-pot3 font-semibold">({{ pot.base }} ×2)</span>
          </span>
          <span class="font-bold text-slate-900">{{ pot.contribution }}</span>
        </div>
      </div>
    </div>

    <div class="mt-3 flex justify-end items-baseline gap-2 pr-1">
      <span class="text-xs uppercase tracking-wider font-semibold text-slate-500">Total</span>
      <span class="text-lg font-extrabold text-slate-900 tabular">{{ props.entry.mainPoints }}</span>
    </div>
  </div>
</template>
